// Header — where you are, what is waiting, and a way to get anywhere.
//
// The old header had a search box and a notification bell that did nothing.
// Both are real now: the box opens ⌘K navigation, and the bell is a live count
// of work waiting, which takes you to the queue.

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from '../../redux/slices/authSlice';
import { FiBell, FiSearch, FiLogOut, FiMenu } from 'react-icons/fi';
import { ROUTES } from '../../constants/app.constants';
import { navItemFor } from '../../constants/navigation';
import { useWorklist } from '../../hooks/useWorklist';
import CommandPalette from './CommandPalette.jsx';

const Header = ({ onMenuToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { data } = useWorklist();
  const [loggingOut, setLoggingOut] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const waiting = Number(data?.total_actions || 0);
  const here = navItemFor(location.pathname);

  // ⌘K / Ctrl+K from anywhere in the panel
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    await dispatch(logoutUser());
    navigate('/login');
  };

  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : 'AD';

  return (
    <>
      <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3 sticky top-0 z-30 shadow-sm shrink-0">
        <button
          onClick={onMenuToggle}
          className="lg:hidden w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-all shrink-0"
          aria-label="Open menu"
        >
          <FiMenu className="w-4 h-4" />
        </button>

        {/* Where you are — so a deep link never leaves you guessing */}
        {here && (
          <p className="hidden lg:block text-sm font-bold text-slate-700 shrink-0">
            {here.label}
          </p>
        )}

        {/* Real search: opens the ⌘K palette */}
        <button
          onClick={() => setPaletteOpen(true)}
          className="hidden sm:flex items-center gap-2 flex-1 max-w-sm lg:ml-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-400 hover:border-slate-300 hover:text-slate-500 transition-all"
        >
          <FiSearch className="w-4 h-4 shrink-0" />
          <span className="truncate">Go to any screen…</span>
          <kbd className="ml-auto text-[10px] font-sans font-bold bg-white border border-slate-200 rounded px-1.5 py-0.5 shrink-0">
            ⌘K
          </kbd>
        </button>

        {/* ml-auto at every breakpoint: the search box stops growing at
            max-w-sm, so without it the slack lands to the right of this group
            and floats it into the middle of the header instead of the edge. */}
        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          {/* Search on small screens */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="sm:hidden w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 shrink-0"
            aria-label="Search"
          >
            <FiSearch className="w-4 h-4" />
          </button>

          {/* Work waiting — a real number, and it goes to the queue */}
          <button
            onClick={() => navigate(ROUTES.TODAY)}
            title={waiting ? `${waiting} thing(s) waiting for you` : 'Nothing waiting — you are clear'}
            className={`relative flex items-center gap-1.5 h-9 px-2.5 rounded-lg border text-sm font-bold transition-all shrink-0 ${
              waiting
                ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
            }`}
          >
            <FiBell className="w-4 h-4" />
            {waiting > 0 && <span className="tabular-nums">{waiting > 99 ? '99+' : waiting}</span>}
          </button>

          <div className="hidden sm:block w-px h-8 bg-slate-200" />

          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-md shrink-0">
              {initials}
            </div>
            <div className="hidden md:block leading-tight">
              <p className="text-sm font-semibold text-slate-800">{user?.username || 'Admin'}</p>
              <p className="text-xs text-slate-400">
                {user?.is_superuser ? 'Super Admin' : 'Administrator'}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100 hover:border-rose-300 text-sm font-medium transition-all shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FiLogOut className={`w-4 h-4 ${loggingOut ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loggingOut ? 'Logging out…' : 'Logout'}</span>
          </button>
        </div>
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
};

export default Header;
